package org.multiplatform.kotfolio.pages.flutter

import androidx.compose.runtime.*
import com.varabyte.kobweb.compose.css.FontSize
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.*
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.silk.components.text.SpanText
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Li
import org.jetbrains.compose.web.dom.Ul
import org.multiplatform.kotfolio.components.KotlinCode
import org.multiplatform.kotfolio.layout.BlogLayout
import org.multiplatform.kotfolio.layout.BlogProperties
import org.multiplatform.kotfolio.layout.PageProperties

@Page("/flutter/asynchronous")
@Composable
fun AsynchronousPage() {
    BlogLayout(
        PageProperties(
            "Asynchronous",
            "Flutter provides async programming model that aims developers to handle asynchronous operations seamlessly. The Flutter FutureBuilder class provides a straightforward way of dealing with asynchronous operations. StreamBuilder is, a powerful builder for efficiently updating your app's UI based on real-time data streams. Learn about Flutter Asynchronous."
        ),
        BlogProperties("Flutter", "future | stream | async | async* | await | FutureBuilder | StreamBuilde")
    ) {
        Column(
            horizontalAlignment = Alignment.Start
        ) {
            SpanText(
                text = "Asynchronous",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Asynchronous data is like waiting for a pizza to be delivered. When you order a pizza, you don’t immediately have the pizza in your hands. You have to wait for the delivery person to bring it to your door.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "While you’re waiting for the pizza to arrive, you can do other things, like watch TV or play games. When the pizza finally arrives, you can stop what you’re doing and enjoy your pizza.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "In the same way, when you request asynchronous data in your code, you don’t immediately have the data available to you. You have to wait for it to be fetched or calculated. During that time, your code can keep doing other things. When the data is finally ready, your code can then handle it and use it to update the UI or perform other tasks.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Synchronous",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Where each instruction is carried out one after the other, waiting for the previous one to finish before moving on to the next. This method ensures a clear and predictable sequence of events, making it suitable for tasks that require a structured and synchronized order of execution.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Thread",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Typically every program is running in a single “thread” of execution. When we have to do a few tasks simultaneously. It’s not suitable to do one by one. Otherwise, if one task is stuck, or return an error, the whole application will freeze on there.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Future",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Long-running tasks are common in mobile apps. The way this is handled in Flutter / Dart is by using a Future. A Future allows you to run work asynchronously to free up any other threads that should not be blocked.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Once the data is ready, a callback will be executed with the data ready to be used.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "It’s like placing an order at a restaurant; you know you’ll get your food eventually, but you don’t know exactly when.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Async and Await",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "async and await both are keyword.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "async means that it can never return data types other than void and Future<T> and will result in a compilation error if you try.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Future such as transforming this into another object. In cases like this, the callback approach will not work because callback values cannot be returned by the function.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                KotlinCode(
                    code = """
                    Future <return_type> _functionName() async {
                      // Do the work in background and return as future
                    }
                    """.trimIndent()
                )

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Async is used in the program to execute the code in the background, you do not need to freeze the UI until the job has done. You can prevent your app is slowing or depending on certain tasks. You can run your app smoothly. While running in the background, the user can decide to do something other.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "await only use inside the async.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Future synchronously is by using the await keyword. When you add a await keyword before a Future value or a function that returns this line will block and wait until the value has been evaluated.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "It suspends the current running function until the result is completed. The result is the completion of the future.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Asynchronous programming works when you are fetching data over the network, Writing data to the database, and reading data from a file.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "FutureBuilder",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "In future builder, it calls the future function to wait for the result, and as soon as it produces the result it calls the builder function where we build the widget.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "FutureBuilder lets you easily determine the current state of a future and show the progress of data loading.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Make sure to check the state of the future with connectionState and display an appropriate widget when the state is busy. It’s a good practice to make sure no connection errors. There are other connection states that we can check.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "It is build itself, and provides the latest AsyncSnapshots. It is working one type of bridge between Futures and the UI.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Stream",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "A Future completes with a single value or an error once, and then it cannot emit more values while A Stream can keep emitting events as long as the source is producing them.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

               Ul {
                   Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                       SpanText(
                           text = "Stream provides an asynchronous sequence of data.",
                           modifier = Modifier
                               .fontSize(FontSize.Medium)
                       )
                   }

                   Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                       SpanText(
                           text = "By subscribing to the stream of messages you are essentially able to get the piece of data as they come in and as they get added to the stream. Dart snapshots method gives us a stream and we can handle the stream using the widgets called StreamBuilder.",
                           modifier = Modifier
                               .fontSize(FontSize.Medium)
                       )
                   }

                   Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                       SpanText(
                           text = "async* will always return a Stream and offer some syntax sugar to emit a value through the yield keyword.",
                           modifier = Modifier
                               .fontSize(FontSize.Medium)
                       )
                   }

                   Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                       SpanText(
                           text = "multiple stream combine the single stream Zip, Groups",
                           modifier = Modifier
                               .fontSize(FontSize.Medium)
                       )
                   }

                   Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                       SpanText(
                           text = "Stream handle error streamController.addError(() => Exception(‘Handle error’));",
                           modifier = Modifier
                               .fontSize(FontSize.Medium)
                       )
                   }

                   Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                       SpanText(
                           text = "Listen() to subscribe to a stream. Access values from a Stream.",
                           modifier = Modifier
                               .fontSize(FontSize.Medium)
                       )
                   }

                   Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                       SpanText(
                           text = "Dispose() to cancel a subscription.",
                           modifier = Modifier
                               .fontSize(FontSize.Medium)
                       )
                   }

                   Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                       SpanText(
                           text = "Close() You can use the controller to shut off the stream in the unlikely event that your information source has run out of things to add to the offer. All onDone callbacks that have been enrolled will be called.",
                           modifier = Modifier
                               .fontSize(FontSize.Medium)
                       )
                   }

                   Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                       SpanText(
                           text = "Stream manipulation map(), asyncMap(), skip(), skipWhile(), take(), takeWhile(), expand(), asyncExpand(), transform()and many more.",
                           modifier = Modifier
                               .fontSize(FontSize.Medium)
                       )
                   }

                   Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                       SpanText(
                           text = "Sigular String, Future, Plural List/Array, Stream",
                           modifier = Modifier
                               .fontSize(FontSize.Medium)
                       )
                   }
               }
            }

            SpanText(
                text = "Stream Manage",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Stream",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            .fontWeight(FontWeight.Bold)
                    )
                }

                Ul {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "This class represents an asynchronous stream of data. Listeners can subscribe to be notified of the arrival of new data events.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }
            }

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "EventSink",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            .fontWeight(FontWeight.Bold)
                    )
                }

                Ul {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "A sink is like a stream that flows in the opposite direction. Adding data events to an EventSink funnels that data into a connected stream.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }
            }

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Sink",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            .fontWeight(FontWeight.Bold)
                    )
                }

                Ul {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "A Sink is an object that receives data and sends it to a destination. In the context of a StreamController, the Sink is used to add data to the stream. You can think of it as a pipe where you can push data into.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "User known as the sink will receive orders from the client and transmit them to the stream. In plain English, the sink will only add data to the StreamController’s stream. Right now, we need to talk about streams. After some processing, it will transmit the data to the outside world.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }
            }

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "StreamController",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            .fontWeight(FontWeight.Bold)
                    )
                }

                Ul {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "A StreamController simplifies stream management, automatically creating a stream and sink, and providing methods for controlling a stream’s behavior.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "It manages the stream, sink, and manages stream events.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = ".StreamController class creates a controller for any stream that is created.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "making it easier for you to use multiple listeners(we’ll get to that soon). Here we’re adding our data using sink.add(), which is an object that accepts data being entered into the stream.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }
            }

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "StreamSubscription",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            .fontWeight(FontWeight.Bold)
                    )
                }

                Ul {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "When we subscribe to any stream we get a stream subscription object using which we can pause, resume or cancel the flow of data that we receive.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "The subscription is a type of StreamSubscription. Once we listen to a stream using Stream.listen, it returns us a StreamSubscription object which can be used to manage the lifecycle of the stream.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Listeners on a stream can save a reference to their subscription, which will allow them to pause, resume, or cancel the flow of data they receive.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "The subscription provides events to the listener, and holds the callbacks used to handle the events.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "The subscription can also be used to unsubscribe from the events, or to temporarily pause the events from the stream.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Here when subscription starts listening to the myStream, one by one all the integer values start flowing into the stream.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Then it will yield that int values one by one asynchronously.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }
            }

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Stream.fromFuture",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            .fontWeight(FontWeight.Bold)
                    )
                }

                Ul {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Creates a new single-subscription stream from the future. When the future completes, the stream will fire one event, either data or error, and then close with a done event.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }
            }

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "ValueStream",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            .fontWeight(FontWeight.Bold)
                    )
                }

                Ul {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "A Stream that provides synchronous access to the last emitted item.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }
            }

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Stream Transformer",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            .fontWeight(FontWeight.Bold)
                    )
                }

                Ul {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "A Stream Transformer allows us to perform data transformations on a Stream. These transformations are then pushed back into the Stream to be received by all the listeners defined for that particular stream.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }
            }

            SpanText(
                text = "Sink Vs Stream",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Sink and Stream are both parts of the StreamController.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Sink is an abstract class that’s implemented by more specific types of sinks.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "A Sink is the entry point of data for a stream.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "StreamController objects are used to define streams and sinks. You can add data to the StreamController using Sink which can be listened to via the Stream.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                    final controller = StreamController<String>();

                    final subscription = controller.stream.listen((String data) {
                      print(data);
                    });

                    controller.sink.add("Sink Added!");
                    """.trimIndent()
            )

            SpanText(
                text = "Types of Stream",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Single Subscription",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            .fontWeight(FontWeight.Bold)
                    )
                }

               Ul {
                   Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                       SpanText(
                           text = "There could be a maximum of one listener to this stream and it listens to values coming out of the stream. It is made up of a set of events that make up a larger whole.",
                           modifier = Modifier
                               .fontSize(FontSize.Medium)
                       )
                   }

                   Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                       SpanText(
                           text = "All events must be delivered in a specific order, with not a single event being missed. When you get a web request or a file, you get this sort of stream. There is only one way to list this stream.",
                           modifier = Modifier
                               .fontSize(FontSize.Medium)
                       )
                   }

                   Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                       SpanText(
                           text = "Listing it over and over results in missed initial values, and the final stream makes little sense. The data is fetched and given in chunks when the listing begins in this stream.",
                           modifier = Modifier
                               .fontSize(FontSize.Medium)
                       )
                   }
               }
            }

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Broadcast",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            .fontWeight(FontWeight.Bold)
                    )
                }

                Ul {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "This stream is for single messages that can be processed one at a time. StreamController.broadcast() There could be an infinite number of listeners to this stream.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "This stream is meant for individual messages that can be handled one at a time. Multiple listeners can listen at a time and it also gives the user the chance to listen after the cancellation of the previous subscription.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }
            }

            KotlinCode(
                code = """
                     //Broadcast Stream
                     final flutterNews = StreamController<String>.broadcast();
                    """.trimIndent()
            )

            SpanText(
                text = "Stream builder",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "A Stream is an order of asynchronous events. When a Stream has emitted all its events, a single “done” event will notify the listener that they reached the end.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "For example, if you are fetching data from an API, the data might not be available immediately, so you need to handle the response asynchronously. In such cases, you can use a stream to listen to events and handle them as soon as they are available.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "StreamBuilder listens to the stream and build itself on every new emitted Event by the stream.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "It will turn our snapshots of data into actual widgets when the new data arrives from the Stream it will be rebuilt.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "It will get done using the set state. In other words, the set state will call every time there’s a new value in the stream.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "It consists of two important things stream and builder.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Stream, in particular, it’s a stream of query snapshots and it knows when new data comes to rebuild itself.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "builder We have to provide the logic for what the stream builder should actually do.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Stream Error Handling",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "cancelOnError",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            .fontWeight(FontWeight.Bold)
                    )
                }

               Ul {
                   Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                       SpanText(
                           text = "If cancelOnError is true, the subscription is automatically canceled when the first error event is delivered. The default is false.",
                           modifier = Modifier
                               .fontSize(FontSize.Medium)
                       )
                   }

                   Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                       SpanText(
                           text = "We can see in the above example that even if an error is encountered, the stream has not stopped its execution. It executed onDone too.",
                           modifier = Modifier
                               .fontSize(FontSize.Medium)
                       )
                   }

                   Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                       SpanText(
                           text = "To close the stream subscription as soon as the error is encountered, we have to give true value to cancelOnError.",
                           modifier = Modifier
                               .fontSize(FontSize.Medium)
                       )
                   }
               }
            }

            SpanText(
                text = "StreamProvider vs StreamBuilder",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "StreamBuilder is a widget that comes with Flutter and rebuilds itself every time the stream gets updated, that’s its only job.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "StreamProvider is a widget that comes with the Provider package, it’s built using StreamBuilder, but combines this with InheritedWidget, allowing you to pass information through the tree of widgets efficiently.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "async vs async*",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(top = 1.em).toAttrs()) {
                    SpanText(
                        text = "async",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            .fontWeight(FontWeight.Bold)
                    )
                }

                Ul{
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "async is your trusty tool for handling a single asynchronous operation.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "It employs ‘await’ and ‘async’ to deal with asynchronous tasks sequentially.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Functions marked as ‘async’ return a ‘Future’ and use ‘return’ to deliver the result once the operation is complete.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Inside the function, you can use await to wait for asynchronous operations to complete without blocking the execution of the function.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }

                Li(Modifier.padding(top = 1.em).toAttrs()) {
                    SpanText(
                        text = "async*",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            .fontWeight(FontWeight.Bold)
                    )
                }

               Ul {

                   Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                       SpanText(
                           text = "Used to create functions that return a Stream of data. Streams are ideal for handling sequences of values that arrive asynchronously over time (e.g., real-time data updates).",
                           modifier = Modifier
                               .fontSize(FontSize.Medium)
                       )
                   }

                   Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                       SpanText(
                           text = "‘async*’ allows you to emit values gradually over time, introducing a dynamic aspect to your code.",
                           modifier = Modifier
                               .fontSize(FontSize.Medium)
                       )
                   }

                   Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                       SpanText(
                           text = "The countdown values (5, 4, 3, 2, 1) are not generated all at once.",
                           modifier = Modifier
                               .fontSize(FontSize.Medium)
                       )
                   }

                   Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                       SpanText(
                           text = "It elegantly returns a ‘Stream’, allowing for the gradual production of values using the yield keyword. (The function can yield multiple values over time using the yield keyword.)",
                           modifier = Modifier
                               .fontSize(FontSize.Medium)
                       )
                   }

                   Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                       SpanText(
                           text = "Unlike ‘async, ‘async*’ is used in functions that emit a stream of values over time rather than returning a single result.",
                           modifier = Modifier
                               .fontSize(FontSize.Medium)
                       )
                   }
               }
            }

            SpanText(
                text = "Parallel Execution",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                    final [user, products] = await Future.wait([ getUser(), getProducts() ]);
                    """.trimIndent()
            )
        }
    }
}